import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button, Card } from "@/components/ui";
import { api, type AdminTenant } from "@/lib/api";
import { normalizeProspectAccess, PROSPECT_FEATURE_KEYS, PROSPECT_FEATURES, type ProspectAccess, type ProspectLevel } from "@logiful/domain";

const LEVEL: Record<ProspectLevel, string> = { none: "gesperrt", view: "ansehen", edit: "bearbeiten" };

function AccessTable({ value, base, onChange, disabled }: { value: ProspectAccess; base?: ProspectAccess; onChange: (v: ProspectAccess) => void; disabled?: boolean }) {
  return (
    <div className="divide-y divide-border rounded-xl border border-border">
      {PROSPECT_FEATURE_KEYS.map((k) => {
        const f = PROSPECT_FEATURES[k];
        const changed = base && base[k] !== value[k];
        return (
          <div key={k} className="flex items-center gap-3 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-medium">{f.label}{changed && <span className="ml-2 rounded-full bg-coral/10 px-1.5 py-px text-[11px] text-coral">abweichend</span>}</p>
              <p className="text-[12px] text-fg-muted">{f.description}</p>
            </div>
            <select
              disabled={disabled}
              aria-label={f.label}
              value={value[k]}
              onChange={(e) => onChange({ ...value, [k]: e.target.value as ProspectLevel })}
              className={`h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px] ${value[k] === "none" ? "text-fg-muted" : "text-fg font-medium"}`}
            >
              {(f.levels as readonly ProspectLevel[]).map((l) => <option key={l} value={l}>{LEVEL[l]}</option>)}
            </select>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Interessenten-Ansicht (nur Logiful): Was sehen selbst registrierte Kunden vor der Freischaltung? Gesperrtes liefert
 * auch die API nicht – nicht nur ausgeblendet. Standard ändern nur Admins; Ausnahmen je Interessent das ganze Team.
 */
export function ProspectAccessCard({ admin, tenants }: { admin: boolean; tenants: AdminTenant[] }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["prospect-access"], queryFn: api.admin.prospectAccess });
  const [draft, setDraft] = useState<ProspectAccess | null>(null);
  const [editing, setEditing] = useState<AdminTenant | null>(null);
  const save = useMutation({
    mutationFn: (v: ProspectAccess) => api.admin.saveProspectAccess(v),
    onSuccess: () => {
      toast.success("Interessenten-Ansicht gespeichert", { description: "Gilt sofort für alle Interessenten ohne eigene Ausnahme." });
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["prospect-access"] });
    },
    onError: () => toast.error("Nicht gespeichert"),
  });
  const prospects = tenants.filter((t) => t.signup && t.testModeSince);
  const defaults = q.data?.defaults;
  if (!defaults) return null;
  const value = draft ?? defaults;

  return (
    <Card className="mt-6 p-5">
      <div className="flex items-start gap-3">
        <EyeOff className="size-5 text-fg-muted mt-0.5" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold">Interessenten-Ansicht</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">
            Was selbst registrierte Kunden sehen, bis ihr sie freischaltet (Testbetrieb beenden). Gesperrte Bereiche liefert auch die Schnittstelle nicht aus. Bestandskunden und euer Team sind nie betroffen. Neue Funktionen sind gesperrt, bis sie hier freigegeben sind.
          </p>
        </div>
      </div>
      <div className="mt-4">
        <AccessTable value={value} onChange={setDraft} disabled={!admin} />
      </div>
      {admin && draft && (
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDraft(null)}>Verwerfen</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(draft)}>Standard speichern</Button>
        </div>
      )}
      <div className="mt-5">
        <h3 className="text-[13px] font-semibold">Aktuelle Interessenten ({prospects.length})</h3>
        {prospects.length ? (
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
            {prospects.map((t) => {
              const n = t.prospectAccess ? Object.keys(t.prospectAccess).length : 0;
              return (
                <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
                  <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                  <span className="text-fg-muted">{n ? `${n} Ausnahme${n === 1 ? "" : "n"}` : "Standard"}</span>
                  <Button size="sm" variant="secondary" onClick={() => setEditing(t)}>Anpassen</Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-1 text-[12.5px] text-fg-muted">Keine – Interessenten erscheinen hier nach der Registrierung.</p>
        )}
      </div>
      {editing && <OverrideDialog tenant={editing} defaults={defaults} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function OverrideDialog({ tenant, defaults, onClose }: { tenant: AdminTenant; defaults: ProspectAccess; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<ProspectAccess>(() => normalizeProspectAccess(tenant.prospectAccess, defaults));
  const save = useMutation({
    mutationFn: (access: ProspectAccess | null) => api.admin.saveTenantProspectAccess(tenant.id, access),
    onSuccess: () => {
      toast.success(`${tenant.name}: Ansicht gespeichert`);
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
      onClose();
    },
    onError: () => toast.error("Nicht gespeichert"),
  });
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(640px,calc(100vw-32px))] max-h-[85vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-5 shadow-xl">
          <Dialog.Title className="text-[16px] font-semibold">Ansicht für {tenant.name}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[12.5px] text-fg-muted">Abweichungen vom Standard gelten nur für diesen Interessenten – z. B. nach dem Erstgespräch mehr zeigen.</Dialog.Description>
          <div className="mt-4">
            <AccessTable value={v} base={defaults} onChange={setV} />
          </div>
          <div className="mt-4 flex justify-between gap-2">
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending}>Auf Standard zurücksetzen</Button>
            <span className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(v)}>Speichern</Button>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
